import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Phone, ArrowLeft, ChevronDown } from 'lucide-react';
import { useState } from 'react';

const faqs = [
  {
    question: "What exactly does Alaska AI Services do?",
    answer: "We provide AI-powered virtual receptionists for service businesses nationwide. Our AI answers your business calls 24/7, qualifies leads, books appointments, and captures customer information - so you never miss a job opportunity, even when you're on a job site or it's after hours."
  },
  {
    question: "How does the AI know about my business?",
    answer: "During setup, we customize the AI specifically for your business. We train it on your services, pricing, service areas, business hours, and how you want calls handled. The AI speaks naturally and represents your business professionally."
  },
  {
    question: "Will callers know they're talking to an AI?",
    answer: "Our AI is designed to be conversational and helpful. While we're transparent that it's an AI assistant, most callers appreciate the instant response and professional service. The AI can always offer to have a human call back for complex situations."
  },
  {
    question: "What happens during an emergency call?",
    answer: "Emergency calls are flagged as high priority. The AI collects critical information (address, issue, contact info) and can immediately notify you via text or email. You'll see emergency leads highlighted in your dashboard."
  },
  {
    question: "How quickly can I get set up?",
    answer: "Setup takes approximately 48 hours. This includes customizing the AI for your business, testing the system, and ensuring everything works perfectly before going live."
  },
  {
    question: "What's included in the $1,997 setup fee?",
    answer: "The setup fee includes: Custom AI training for your specific business, integration with your phone system, calendar booking automation, your client dashboard access, 30 days of optimization, and our 80% accuracy guarantee."
  },
  {
    question: "Is the $397/month maintenance required?",
    answer: "No, the monthly maintenance is optional. It includes ongoing AI optimization, weekly performance reports, priority support, and new feature updates. Many customers choose it for peace of mind, but your AI will continue working without it. Annual plans save you 2 months (pay for 10, get 12)."
  },
  {
    question: "What's the 80% accuracy guarantee?",
    answer: "We guarantee that within 30 days, your AI will correctly handle at least 80% of calls (proper qualification, accurate information capture, appropriate responses). If we don't hit this target, you get a full refund AND keep the system."
  },
  {
    question: "Can I see my leads and call data?",
    answer: "Yes! You get access to a client dashboard where you can see all captured leads, appointment bookings, call statistics, and conversation summaries in real-time."
  },
  {
    question: "What types of businesses do you work with?",
    answer: "We work with service businesses across all 50 states including HVAC, plumbing, electrical, roofing, landscaping, auto repair, dental offices, fishing charters, tour operators, restaurants, gyms, and more. If you take customer calls, we can help."
  },
  {
    question: "Do you serve my state?",
    answer: "Yes! While we're headquartered in Alaska, we serve contractors and service businesses in all 50 states. Our AI works with any US phone system and can be customized for regional differences in how customers communicate."
  },
  {
    question: "How do I get started?",
    answer: "Simply book a free 10-minute demo on our website. We'll show you exactly how the AI works for your industry, answer your questions, and if you're ready, we can start setup the same day."
  },
  {
    question: "Can I cancel anytime?",
    answer: "Yes. If you have the monthly maintenance plan, you can cancel anytime with no long-term commitment. Your AI setup is yours to keep regardless."
  }
];

function FAQItem({ question, answer }) {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="border-b border-slate-800">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full py-5 flex items-center justify-between text-left hover:text-sky-400 transition-colors"
      >
        <span className="font-medium text-lg pr-8">{question}</span>
        <ChevronDown className={`w-5 h-5 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          className="pb-5"
        >
          <p className="text-slate-400 leading-relaxed">{answer}</p>
        </motion.div>
      )}
    </div>
  );
}

export default function FAQPage() {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-900/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/')}
              className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-sky-400 to-blue-600 flex items-center justify-center">
                <Phone className="w-4 h-4 text-white" />
              </div>
              <span className="font-bold text-lg">Alaska AI Services</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-4xl font-bold mb-4">Frequently Asked Questions</h1>
          <p className="text-slate-400 text-lg mb-12">
            Everything you need to know about Alaska AI Services
          </p>

          <div className="space-y-0">
            {faqs.map((faq, index) => (
              <FAQItem key={index} question={faq.question} answer={faq.answer} />
            ))}
          </div>

          <div className="mt-12 p-6 bg-slate-900/50 border border-slate-800 rounded-xl text-center">
            <h3 className="text-xl font-semibold mb-2">Still have questions?</h3>
            <p className="text-slate-400 mb-4">We're here to help</p>
            <a 
              href="mailto:info@alaskaaiservices.com"
              className="text-sky-400 hover:underline"
            >
              info@alaskaaiservices.com
            </a>
          </div>
        </motion.div>
      </main>
    </div>
  );
}
